/**
 * Learn more about createBottomTabNavigator:
 * https://reactnavigation.org/docs/bottom-tab-navigator
 */

import { createStackNavigator } from '@react-navigation/stack';
import { View } from 'react-native';
import * as React from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Modalize } from 'react-native-modalize';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNetInfo } from '@react-native-community/netinfo';

import Loading from 'components/Loading';
import ActionModal from 'components/ActionModal';
import MenuModal from 'components/MenuModal';
import AppContext from 'contexts/AppContext';
import OrderContext from 'contexts/OrderContext';
import LoginScreen from 'screens/LoginScreen';
import OrdersScreen from 'screens/OrdersScreen';
import OrderDetailsScreen from 'screens/OrderDetailsScreen';
import VenueSelectionScreen from 'screens/VenueSelectionScreen';
import ShoppingScreen from 'screens/ShoppingScreen';
import BillingScreen from 'screens/BillingScreen';
import CommercesScreen from 'screens/CommercesScreen';
import ConsumptionsReportsScreen from 'screens/ConsumptionsReportsScreen';
import VisualizationScreen from 'screens/VisualizationScreen';
import AccountBalanceScreen from 'screens/AccountBalanceScreen';
import CloverConnectionReportsScreen from 'screens/CloverConnectionReportsScreen';

import Order from 'types/models/Order';
import ModalData from 'types/models/ModalData';
import AuthData from 'types/models/AuthData';
import Venue from 'types/models/Venue';
import Commerce from 'types/models/Commerce';
import ModalizeData from 'types/models/ModalizeData';
import { PrivateStackParamList, PublicStackParamList } from 'types/navigation/stackNavigator';
import constantsStorage from 'constants/Storage';
import { useTranslation } from 'react-i18next';
import RefundsScreen from 'screens/RefundsScreen';
import Storage from 'constants/Storage';
import { getHideCloverMonitoringConfig } from 'services/systemConfigService';

export default function BottomTabNavigator(): JSX.Element | null {
  const { i18n } = useTranslation();
  const Stack = createStackNavigator();
  const modalizeRef = React.useRef<Modalize>(null);
  const [initialLoad, setInitialLoad] = React.useState<boolean>(false);
  const [displayMenuModal, setDisplayMenuModal] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [authData, setAuthData] = React.useState<AuthData | null>(null);
  const [modalData, setModalData] = React.useState<ModalData | null>(null);
  const [modalizeData, setModalizeData] = React.useState<ModalizeData | null>(null);
  const [hideCloverMonitoring, setHideCloverMonitoring] = React.useState<boolean>(false);

  React.useEffect(() => {
    getHideCloverMonitoringConfig().then((config) => {
      setHideCloverMonitoring(config.value);
    });
  }, []);

  React.useEffect(() => {
    const getLanguage = async () => {
      try {
        const storedLanguage = await AsyncStorage.getItem(Storage.language);
        i18n.changeLanguage(storedLanguage || i18n.language);
      } catch (error) {
        console.error('Error al obtener el idioma:', error);
      }
    };

    getLanguage();
  }, []);

  const setStorageAuthData = async (): Promise<void> => {
    const authData = await AsyncStorage.getItem(constantsStorage.authData);
    if (authData) setAuthData(JSON.parse(authData));
  };

  const loadInitalData = React.useCallback(async () => {
    await setStorageAuthData();
    setInitialLoad(true);
  }, []);

  React.useEffect(() => {
    if (modalizeRef.current) {
      if (modalizeData) {
        modalizeRef.current.open();
      } else {
        modalizeRef.current.close();
      }
    }
  }, [modalizeData]);

  React.useEffect(() => {
    loadInitalData();
  }, []);

  if (!initialLoad) {
    return <Loading />;
  } else {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: '#FFFFFF' }}>
        <AppContext.Provider
          value={{
            isLoading,
            setIsLoading,
            setAuthData,
            authData,
            setModalData,
            modalizeData,
            setModalizeData,
            expoPushToken: null,
            displayMenuModal,
            setDisplayMenuModal,
            hideCloverMonitoring,
          }}
        >
          {isLoading && <Loading />}
          <Stack.Navigator>
            {authData ? (
              <Stack.Screen name="Private" component={PrivateNavigator} options={{ headerShown: false }} />
            ) : (
              <Stack.Screen name="Public" component={PublicNavigator} options={{ headerShown: false }} />
            )}
          </Stack.Navigator>
          {modalData && <ActionModal modalData={modalData} />}
        </AppContext.Provider>
        {modalizeData && (
          <Modalize
            ref={modalizeRef}
            adjustToContentHeight={true}
            disableScrollIfPossible={true}
            tapGestureEnabled={false}
            panGestureEnabled={false}
            withHandle={false}
            panGestureComponentEnabled={false}
            closeOnOverlayTap={modalizeData.closeOnOverlayTap}
            onClose={() => {
              setModalizeData(null);
            }}
          >
            <View>{modalizeData.content}</View>
          </Modalize>
        )}
      </SafeAreaView>
    );
  }
}
const PrivateStack = createStackNavigator<PrivateStackParamList>();

const PrivateNavigator = (): JSX.Element => {
  const netInfo = useNetInfo();
  const { displayMenuModal } = React.useContext(AppContext);
  const isConnected = netInfo.type !== 'unknown' && netInfo.isConnected;
  const [initialLoad, setInitialLoad] = React.useState<boolean>(false);
  const [currentVenue, setCurrentVenue] = React.useState<Venue | null>(null);
  const [currentOrder, setCurrentOrder] = React.useState<Order | null>(null);
  const [currentCommerce, setCurrentCommerce] = React.useState<Commerce | null>(null);

  const setStorageVenueData = async (): Promise<void> => {
    const currentVenue = await AsyncStorage.getItem(constantsStorage.currentVenue);
    if (currentVenue) setCurrentVenue(JSON.parse(currentVenue));
  };

  const loadInitalData = React.useCallback(async () => {
    await setStorageVenueData();
    setInitialLoad(true);
  }, []);

  React.useEffect(() => {
    loadInitalData();
  }, []);

  if (!initialLoad) {
    return <Loading />;
  } else {
    return (
      <OrderContext.Provider
        value={{
          currentVenue,
          setCurrentVenue,
          currentOrder,
          setCurrentOrder,
          currentCommerce,
          setCurrentCommerce,
          isConnected,
        }}
      >
        <MenuModal visible={displayMenuModal} />
        <PrivateStack.Navigator screenOptions={{ title: 'Cashless xFanXP' }}>
          <PrivateStack.Screen
            name="VenueSelectionScreen"
            component={VenueSelectionScreen}
            options={{ headerShown: false }}
          />
          <PrivateStack.Screen name="CommercesScreen" component={CommercesScreen} options={{ headerShown: false }} />
          <PrivateStack.Screen
            name="ConsumptionsReportsScreen"
            component={ConsumptionsReportsScreen}
            options={{ headerShown: false }}
          />
          <PrivateStack.Screen name="RefundsScreen" component={RefundsScreen} options={{ headerShown: false }} />
          <PrivateStack.Screen name="OrdersScreen" component={OrdersScreen} options={{ headerShown: false }} />
          <PrivateStack.Screen
            name="OrderDetailsScreen"
            component={OrderDetailsScreen}
            options={{ headerShown: false }}
          />
          <PrivateStack.Screen
            name="VisualizationScreen"
            component={VisualizationScreen}
            options={{ headerShown: false }}
          />
          <PrivateStack.Screen
            name="AccountBalanceScreen"
            component={AccountBalanceScreen}
            options={{ headerShown: false }}
          />
          <PrivateStack.Screen name="ShoppingScreen" component={ShoppingScreen} options={{ headerShown: false }} />
          <PrivateStack.Screen name="BillingScreen" component={BillingScreen} options={{ headerShown: false }} />
          <PrivateStack.Screen
            name="CloverConnectionReportsScreen"
            component={CloverConnectionReportsScreen}
            options={{ headerShown: false }}
          />
        </PrivateStack.Navigator>
      </OrderContext.Provider>
    );
  }
};

const PublicStack = createStackNavigator<PublicStackParamList>();

const PublicNavigator = (): JSX.Element => {
  return (
    <PublicStack.Navigator screenOptions={{ title: 'Cashless xFanXP' }}>
      <PublicStack.Screen name="LoginScreen" component={LoginScreen} options={{ headerShown: false }} />
    </PublicStack.Navigator>
  );
};
